/* eslint-disable no-restricted-globals */
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import UserProfileHeader from '../../components/UserProfileHeader/UserProfileHeader';
import {
  fetchUserInformationByIdV1,
  getUserAssignedScheduler,
} from 'src/api/user';
import { LoadingIndicator } from '../../components';
import NoData from '../../components/NoData';
import {
  clearQuestionnaireMetaData,
  clearUserProfileMetaData,
  fetchTherapistClientMetaData,
  fetchTherapistClientProfileMetaData,
  fetchUserProfileMetaData,
  removeTherapistClientProfileMetaData,
} from 'src/redux/actions/questionnaireActions';
import UserProfileContent from '../../components/UserProfileContent/UserProfileContent';
import {
  ClientTabs,
  SchedulerAndAdminTabs,
  TherapistTabs,
} from '../../components/UserProfileHeader/AccessControls';
import {
  User,
  UserProfileTab,
} from '../../components/UserProfileHeader/types';
import {
  clearModalContent,
  setModalContent,
} from 'src/redux/modules/modalEvent';
import ConfirmationModalContainer from 'src/containers/ConfirmationModal/ConfirmationModalContainer';
import { deepClone, findDependentPage } from 'src/utils/helpers';
import { fetchQuestionsOfCurrentPage } from 'src/redux/services/questionnaireServices';
import QuestionnaireModal from 'src/containers/questionnaireModalContainer/QuestionnaireModalContainer';
import TherapistAvailabilityModal from '../../components/TherapistAvailabilityModal/TherapistAvailabilityModal';
import { useIsMobile } from "src/latest/src/hooks";
import Button from 'src/components/base/Button/Button';
import { ProfileTab } from '../../components/UserProfileHeader/constant';
import './ProfilePage.scss';
import { UserRoles } from '../../constants/UserRoles';
import { TherapistAvailabilityWidgetEnum } from '../../components/TherapistAvailabilityWidget/TherapistAvailabilityWidgetEnum';
import { UserServiceSection } from '../../constants/AppServerConfig';

const ProfilePage = () => {
  const isFromMobile = useIsMobile();
  const { profileId } = useParams<string>();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(true);
  const [isPageAvailableToView, setIsPageAvailableToView] = useState(false);
  const [userInfo, setUserInfo] = useState<User>();
  const [profileTab, setProfileTab] = useState<string>(ProfileTab.hash);
  const [assignedTo, setassignedTo] = useState();
  const loggedInUser = useSelector(
    (state: any) => state?.authentication?.loggedInUser,
  );
  const profileDetails = useSelector(
    (state: any) => state?.questionnaireData?.currentUserProfileMetaData,
  );
  const pageMetadata = useSelector(
    (state: any) => state?.questionnaireData?.questionnairePageData,
  );
  const isContinueButtonDisabled = useSelector(
    (state: any) =>
      state?.questionnaireData?.currentConfig?.isContinueButtonDisabled,
  );
  const sessionId = useSelector(
    (state: any) =>
      state?.questionnaireData?.questionnairePageData
        ?.questionnaire_completion_session_id,
  );
  const [questionModalStatus, setQuestionnaureModalStatus] = useState(false);
  const [questionnaireData, setQuestionnaireData] = useState({});
  const [pageTitle, setPageTitle] = useState('');
  const [currentPage, setCurrentPage] = useState<any>();
  const [showModal, setShowModal] = useState(false);
  const [showAvailibilityWidget, setShowAvailibilityWidget] = useState(false);
  const [userTabs, setUserTabs] = useState<UserProfileTab[]>([]);
  const [selectedTabConfig, setSelectedTabConfig] = useState<
    UserProfileTab | undefined
  >();

  useEffect(() => {
    if (profileTab) {
      const tabConfig = userTabs.find(tab => tab.hash === profileTab);

      setSelectedTabConfig(tabConfig);
    }
  }, [profileTab, userTabs]);

  useEffect(() => {
    if (profileId) {
      init(profileId, loggedInUser);
    }

    return () => {
      dispatch(removeTherapistClientProfileMetaData());
      dispatch(clearUserProfileMetaData());
    };
  }, [profileId]);

  useEffect(() => {
    if (userInfo) {
      setProfileTabHash(userInfo);
      initUserMetadata(userInfo);
      handlePageSectionAvalibility(userInfo, loggedInUser);
      setTabConfigs(userInfo);
    }
  }, [userInfo]);

  useEffect(() => {
    if (location.hash && userInfo) {
      setProfileTabHash(userInfo);
    }
  }, [location.hash]);

  const init = async (profileId: string, userData?: User) => {
    if (profileId === userData?.id) {
      dispatch(clearUserProfileMetaData());
      setUserInfo(userData);
      setIsLoading(false);
    } else {
      fetchUserInfo(profileId);
    }
  };

  const initUserMetadata = async (userData: User) => {
    if (userData?.user_type?.includes(UserRoles.CLIENT)) {
      initClientData(userData?.id);
    } else {
      initStaffData(userData?.id);
    }
  };

  const initClientData = async (profileId: string) => {
    // The client page is restricted for guests; details are fetched only after login.
    if (profileId && loggedInUser?.id) {
      await dispatch(fetchTherapistClientProfileMetaData(profileId));
      await dispatch(fetchTherapistClientMetaData(profileId));
      await getAssignedScheduler();
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  };

  const initStaffData = async (profileId: string) => {
    // The staff page is restricted for guests, except for THERAPISTS; details are fetched only after login.
    if (
      profileId &&
      (loggedInUser?.id || userInfo?.user_type?.includes('THERAPIST'))
    ) {
      await dispatch(
        fetchUserProfileMetaData(true, initDataCallback, profileId),
      );
      await dispatch(fetchTherapistClientMetaData(profileId, true));
      setIsLoading(false);
    } else {
      setIsLoading(false);
    }
  };

  // Sets the appropriate tabs based on the user's role(s)
  const setTabConfigs = (userInfo: User): void => {
    if (userInfo?.user_type.includes(UserRoles.CLIENT)) {
      setUserTabs(ClientTabs);
    } else if (userInfo?.user_type.includes(UserRoles.THERAPIST)) {
      setUserTabs(TherapistTabs);
    } else if (
      userInfo?.user_type.includes(UserRoles.PRACTICE_ADMIN) ||
      userInfo?.user_type.includes(UserRoles.SCHEDULER)
    ) {
      setUserTabs(SchedulerAndAdminTabs);
    }
  };

  // Sets the appropriate profile hash on the URL based on the user's role and
  // If hash are valid or not.
  const setProfileTabHash = (userInfo: User) => {
    const profileTabHash = location.hash.replace('#', '');
    let validHash = false;
    let tabsForProfile: UserProfileTab[] = [];

    if (userInfo?.user_type.includes(UserRoles.CLIENT)) {
      tabsForProfile = ClientTabs;
    } else if (userInfo?.user_type.includes(UserRoles.THERAPIST)) {
      tabsForProfile = TherapistTabs;
    } else {
      tabsForProfile = SchedulerAndAdminTabs;
    }

    const tabConfig: any = tabsForProfile.find(
      item => item.hash === profileTabHash,
    );

    if (tabConfig?.isVisible) {
      validHash = tabConfig.isVisible(userInfo, loggedInUser);
    }

    if (profileTabHash && validHash) {
      setProfileTab(profileTabHash);
    } else {
      setProfileTab('profile');
    }
  };

  const handlePageSectionAvalibility = (userInfo: User, loggedInUser: User) => {
    // This condition ensures the profile page is accessible only if:
    // - The user is a therapist, or
    // - The user is logged in and viewing either their own profile or another user's profile.
    setIsPageAvailableToView(
      userInfo?.user_type?.includes('THERAPIST') || !!loggedInUser?.id,
    );

    // Show the availability widget only if:
    // - The user is a therapist, and
    // - The logged-in user is either not available (guest) or is a client.
    setShowAvailibilityWidget(
      userInfo?.user_type?.includes('THERAPIST') &&
        (!loggedInUser?.id || loggedInUser?.user_type?.includes('CLIENT')),
    );
  };

  const fetchUserInfo = async (profileId: string) => {
    if (profileId) {
      try {
        const selectedUserData = await fetchUserInformationByIdV1(
          profileId,
          UserServiceSection.profile_display,
        );

        setUserInfo(selectedUserData);
      } catch (err) {

        console.error(err);

        setIsLoading(false);
      }
    }
  };

  const getAssignedScheduler = async () => {
    const assinedScheduler = await getUserAssignedScheduler(profileId);

    setassignedTo(assinedScheduler);
  };

  const initDataCallback = () => {
    if (questionModalStatus) {
      dispatch(clearQuestionnaireMetaData());
      setQuestionnaireData({});
      setQuestionnaureModalStatus(!questionModalStatus);
    }
  };

  const showPage = async (pageId: string) => {
    setQuestionnaureModalStatus(true);
    const currentMetadata = deepClone(pageMetadata);
    const allPages: any[] = [];

    currentMetadata?.questionnaire_group?.categories?.forEach((category: any) => {
      category?.pages.forEach((p: any) => {
        allPages.push(p);
      });
    });
    
    const selectedCurrentPage = allPages?.filter(p => p.id === pageId);

    currentMetadata.questionnaire_group.categories[0].pages =
      selectedCurrentPage;

    const currentPageTitle =
      currentMetadata.questionnaire_group.categories[0].pages[0]?.name;

    setPageTitle(currentPageTitle);
    setCurrentPage(selectedCurrentPage[0]);
    const { questions } = await fetchQuestionsOfCurrentPage(pageId, sessionId);

    if ((!questions || questions.length === 0) && profileId) {
      initStaffData(profileId);
    } else {
      currentMetadata.questionnaire_group.categories[0].pages[0].questions =
        questions;
      setQuestionnaireData(currentMetadata);
    }
  };

  const toggleQuestionnaireModalStatus = () => {
    if (questionModalStatus && !isContinueButtonDisabled) {
      // this confirmation modal is only visible when there are some changes made the user on the current question
      dispatch(
        setModalContent({
          modalContent: (
            <div>
              <ConfirmationModalContainer
                clickedYes={() => {
                  dispatch(clearModalContent());
                  setQuestionnaireData({});
                  setQuestionnaureModalStatus(!questionModalStatus);
                }}
                clickedOnNo={() => dispatch(clearModalContent())}
                dynamicText={`Are you sure you want to close <b>Edit ${pageTitle}</b> without saving changes?`}
              />
            </div>
          ),
          size: 'lg',
          isWithCancelButton: false,
          isWithCloseButton: true,
          isGeneralPopup: true,
          className: 'confirmation-modal',
          title: 'Confirm',
        }),
      );
    } else {
      dispatch(clearQuestionnaireMetaData());
      setQuestionnaireData({});
      setQuestionnaureModalStatus(!questionModalStatus);
    }
  };

  const onResponseSaved = async (currentQuestions: Array<any>) => {
    let allPages: any[] = [];
    const currentMetadata = deepClone(pageMetadata);

    // changed .map() to .forEach() below to remove the eslint warnings.
    currentMetadata?.questionnaire_group?.categories?.forEach(
      (category: any, index: number) => {
        category?.pages.map((p: any) => {
          p.sequence = index * 1000 + p.sequence;
          allPages.push(p);

          return p;
        });
      },
    );

    allPages = allPages.sort((a, b) => a.sequence - b.sequence);

    let nextPage = findDependentPage(currentPage, allPages, currentQuestions);

    // Hide next page
    if (
      userInfo?.status === 'Pre-onboard Complete' &&
      currentPage?.unique_key === 'expertisePage'
    ) {
      nextPage = null;
    }

    if (!nextPage && profileId) {
      initStaffData(profileId);
    } else {
      showPage(nextPage.id);
    }
  };

  return (
    <div className="profile-page-container">
      {isLoading ? (
        <div style={{ height: '200px' }}>
          <LoadingIndicator loading={isLoading} />
        </div>
      ) : (
        <>
          {isPageAvailableToView ? (
            <>
              {userInfo &&
              selectedTabConfig?.isVisible &&
              selectedTabConfig?.isVisible(userInfo, loggedInUser) ? (
                <>
                  <UserProfileHeader
                    userProfileDetails={userInfo}
                    onUpdateUserDetails={setUserInfo}
                    selectedTab={profileTab}
                    userTabs={userTabs}
                  />
                  <div className="profile-page-content">
                    <QuestionnaireModal
                      onResponseSaved={onResponseSaved}
                      questionnaireData={questionnaireData}
                      modalTitle={pageTitle}
                      questionModalStatus={questionModalStatus}
                      toggleQuestionnaireModalStatus={
                        toggleQuestionnaireModalStatus
                      }
                    />
                    <UserProfileContent
                      showPage={showPage}
                      selectedTab={profileTab}
                      userProfileDetails={userInfo}
                      profileDetails={profileDetails}
                      assignedToSch={assignedTo}
                    />
                    <TherapistAvailabilityModal
                      profileId={profileId}
                      showModal={showModal}
                      setShowModal={setShowModal}
                    />
                    <div
                      className={`${
                        showAvailibilityWidget && isFromMobile
                          ? 'app-bottom-fixed-button'
                          : 'd-none'
                      }`}
                    >
                      <Button
                        label={
                          TherapistAvailabilityWidgetEnum.headingTextForAvailabilityWidget
                        }
                        onClick={() => {
                          setShowModal(true);
                        }}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="mt-5">
                  <NoData message="No user found or invalid page" />
                </div>
              )}
            </>
          ) : (
            <div className="mt-5">
              <NoData message="No user found or invalid page" />
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default ProfilePage;
